import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Search, Globe, FileText, Loader2, Save } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSEO = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seoData, setSeoData] = useState({
    site_title: '',
    site_description: '',
    site_keywords: '',
    site_author: '',
    og_title: '',
    og_description: '',
    og_image: '',
    og_url: '',
    twitter_card: 'summary_large_image',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
    robots_allow_all: true,
    robots_custom_rules: '',
    sitemap_priority_home: 1.0,
    sitemap_priority_construction: 0.8,
    sitemap_priority_other: 0.6,
    sitemap_changefreq: 'weekly',
    google_analytics_id: '',
    google_tag_manager_id: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSEOSettings();
  }, [navigate]);

  const fetchSEOSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/seo-settings`);
      setSeoData(response.data);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');

    try {
      await axios.put(`${BACKEND_URL}/api/admin/seo-settings`, seoData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "SEO Settings Saved",
        description: "All SEO configurations updated successfully"
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: error.response?.data?.detail || "Failed to save SEO settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SEO Management</h1>
                <p className="text-sm text-gray-600">Configure website SEO settings</p>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-orange-500 to-orange-600"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Tabs defaultValue="meta" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="meta">
              <Search className="w-4 h-4 mr-2" />
              Meta Tags
            </TabsTrigger>
            <TabsTrigger value="social">
              <Globe className="w-4 h-4 mr-2" />
              Social Media
            </TabsTrigger>
            <TabsTrigger value="technical">
              <FileText className="w-4 h-4 mr-2" />
              Technical SEO
            </TabsTrigger>
            <TabsTrigger value="analytics">
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Meta Tags Tab */}
          <TabsContent value="meta" className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Basic Meta Tags</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site Title *
                  </label>
                  <Input
                    value={seoData.site_title}
                    onChange={(e) => setSeoData({...seoData, site_title: e.target.value})}
                    placeholder="चांगुणा आंगण | Changuna Aangan..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Recommended: 50-60 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description *
                  </label>
                  <Textarea
                    value={seoData.site_description}
                    onChange={(e) => setSeoData({...seoData, site_description: e.target.value})}
                    rows={3}
                    placeholder="Brief description of your website..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 150-160 characters | Current: {seoData.site_description.length}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keywords (comma-separated)
                  </label>
                  <Input
                    value={seoData.site_keywords}
                    onChange={(e) => setSeoData({...seoData, site_keywords: e.target.value})}
                    placeholder="real estate, thane, diva junction, 1 bhk..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <Input
                    value={seoData.site_author}
                    onChange={(e) => setSeoData({...seoData, site_author: e.target.value})}
                    placeholder="Samrin Dreamspace Pvt Ltd"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Social Media Tab */}
          <TabsContent value="social" className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Open Graph (Facebook) Tags</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG Title</label>
                  <Input
                    value={seoData.og_title}
                    onChange={(e) => setSeoData({...seoData, og_title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG Description</label>
                  <Textarea
                    value={seoData.og_description}
                    onChange={(e) => setSeoData({...seoData, og_description: e.target.value})}
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG Image URL</label>
                  <Input
                    value={seoData.og_image}
                    onChange={(e) => setSeoData({...seoData, og_image: e.target.value})}
                    placeholder="/images/building-hero.jpg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG URL</label>
                  <Input
                    value={seoData.og_url}
                    onChange={(e) => setSeoData({...seoData, og_url: e.target.value})}
                    placeholder="http://changunaaangan.com"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Twitter Card Tags</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Card Type</label>
                  <select
                    value={seoData.twitter_card}
                    onChange={(e) => setSeoData({...seoData, twitter_card: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="summary">Summary</option>
                    <option value="summary_large_image">Summary Large Image</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Title</label>
                  <Input
                    value={seoData.twitter_title}
                    onChange={(e) => setSeoData({...seoData, twitter_title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Description</label>
                  <Textarea
                    value={seoData.twitter_description}
                    onChange={(e) => setSeoData({...seoData, twitter_description: e.target.value})}
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Image</label>
                  <Input
                    value={seoData.twitter_image}
                    onChange={(e) => setSeoData({...seoData, twitter_image: e.target.value})}
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Technical SEO Tab */}
          <TabsContent value="technical" className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Robots.txt Configuration</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={seoData.robots_allow_all}
                    onChange={(e) => setSeoData({...seoData, robots_allow_all: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-gray-700">Allow all search engines to crawl</label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Robots Rules (Optional)
                  </label>
                  <Textarea
                    value={seoData.robots_custom_rules}
                    onChange={(e) => setSeoData({...seoData, robots_custom_rules: e.target.value})}
                    rows={6}
                    placeholder="User-agent: *\nDisallow: /admin/"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty to use default rules</p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Preview:</strong> Your robots.txt will be available at: 
                    <a href="/robots.txt" target="_blank" className="ml-2 underline">
                      {seoData.og_url}/robots.txt
                    </a>
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Sitemap Configuration</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Homepage Priority
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={seoData.sitemap_priority_home}
                      onChange={(e) => setSeoData({...seoData, sitemap_priority_home: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Construction Page Priority
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={seoData.sitemap_priority_construction}
                      onChange={(e) => setSeoData({...seoData, sitemap_priority_construction: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Other Pages Priority
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={seoData.sitemap_priority_other}
                      onChange={(e) => setSeoData({...seoData, sitemap_priority_other: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Frequency
                  </label>
                  <select
                    value={seoData.sitemap_changefreq}
                    onChange={(e) => setSeoData({...seoData, sitemap_changefreq: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="always">Always</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Preview:</strong> Your sitemap will be available at: 
                    <a href="/sitemap.xml" target="_blank" className="ml-2 underline">
                      {seoData.og_url}/sitemap.xml
                    </a>
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Analytics Integration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Analytics ID (GA4)
                  </label>
                  <Input
                    value={seoData.google_analytics_id || ''}
                    onChange={(e) => setSeoData({...seoData, google_analytics_id: e.target.value})}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: G-ABC123DEF4</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Tag Manager ID
                  </label>
                  <Input
                    value={seoData.google_tag_manager_id || ''}
                    onChange={(e) => setSeoData({...seoData, google_tag_manager_id: e.target.value})}
                    placeholder="GTM-XXXXXXX"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: GTM-ABC1234</p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> After adding Analytics IDs, you'll need to rebuild and redeploy the frontend.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-3">
                <a
                  href={`${seoData.og_url}/robots.txt`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  → View Robots.txt
                </a>
                <a
                  href={`${seoData.og_url}/sitemap.xml`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  → View Sitemap.xml
                </a>
                <a
                  href="https://search.google.com/search-console"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  → Google Search Console
                </a>
                <a
                  href="https://www.bing.com/webmasters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 hover:underline"
                >
                  → Bing Webmaster Tools
                </a>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button at Bottom */}
        <div className="mt-8 text-center">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 px-12"
          >
            {saving ? 'Saving...' : 'Save All SEO Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSEO;
